import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  FormControl,
  Grid,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import { memo, useCallback } from "react";
import { Neutrals } from "../../../../../core-components/theme/color-palette/colors";
import CustomAsyncAutocomplete from "../../../../../core-components/theme/components/CustomAsyncAutoComplete";
import UnitInput from "../../../../../core-components/theme/components/UnitInput";
import { timeOptions } from "../../../../TaskTemplate/helper/fetchHelpers";
import { EscalationGroup, User } from "../../../types";


interface EscalationGroupRowProps {
  group: EscalationGroup;
  index: number;
  errors: { users?: string; time?: string };
  canRemove: boolean;
  onUpdate: (groupId: string, updates: Partial<EscalationGroup>) => void;
  onRemove: (groupId: string) => void;
  options: any
}

const renderOption = (option: any) =>
  <div>
    <div style={{ fontWeight: 500 }}>{option.name}</div>
    <div style={{ fontSize: "0.875rem", color: "#666" }}>
      {option.role} • {option.email}
    </div>
  </div>

const EscalationGroupRow = memo(
  ({
    group,
    index,
    errors,
    canRemove,
    options,
    onUpdate,
    onRemove,
  }: EscalationGroupRowProps) => {

    const handleTimeChange = useCallback(
      (time: { value: string | number; unit: string }) => {
        onUpdate(group.id, {
          value: String(time.value),
          unit: time.unit as 'Minute(s)' | 'Hour(s)' | 'Day(s)' | 'Week(s)',
        });
      },
      [group.id, onUpdate]
    );

    const handleUsersSelect = useCallback(
      (selectedUsers: User[]) => {
        onUpdate(group.id, { users: selectedUsers || [] });
      },
      [group.id, onUpdate]
    );

    const handleRemoveClick = useCallback(() => {
      onRemove(group.id);
    }, [group.id, onRemove]);

    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: 1,
          borderColor: "divider",
          borderRadius: 2,
          backgroundColor: "background.paper",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="subtitle2">
            Escalation Group {index + 1}
          </Typography>
          {canRemove && (
            <IconButton
              onClick={handleRemoveClick}
              size="small"
              sx={{
                width: 24,
                height: 24,
                backgroundColor: Neutrals[50],
                "&:hover": { backgroundColor: "#E0E0E0" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        <Grid container spacing={3} alignItems="flex-start">

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <CustomAsyncAutocomplete
                label="User Name(s)"
                placeholder="Select users..."
                value={group.users}
                labelKey="name"
                mainKey="_id"
                defaultOptions={options}
                multiple
                onSelect={handleUsersSelect}
                error={!!errors.users}
                errorText={errors.users}
                renderCustomOption={renderOption}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <UnitInput
              value={{ value: group.value, unit: group.unit }}
              onChange={handleTimeChange}
              unitOptions={timeOptions}
              error={!!errors.time}
              errorText={errors.time}
            />
          </Grid>

        </Grid>
      </Paper>
    );
  }
);

export default EscalationGroupRow;